import { memo, useMemo, useState } from 'react';
import { Table } from 'rsuite';
import { ButtonSecondary } from '../../../../../../components/ButtonSecondary';
import { SearchInput } from '../../../../../../components/SearchInput';
import removeRecipientIcon from '../../SetupIpaPricingResponsibilities/assets/remove-assignment.svg';
import { EngineUpdateRecipient } from '../types';
import styles from '../SetupIpaEngineUpdateNotifications.module.scss';

const Pagination = memo(Table.Pagination);

// AC-8: página de 10 destinatários; a paginação só aparece a partir da 11ª linha.
const PAGE_SIZE = 10;

type EngineUpdateRecipientsBlockProps = {
  recipients: EngineUpdateRecipient[];
  onRemove: (recipient: EngineUpdateRecipient) => void;
  onAddClick: () => void;
};

export const EngineUpdateRecipientsBlock = ({ recipients, onRemove, onAddClick }: EngineUpdateRecipientsBlockProps) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const sortedRecipients = useMemo(
    () => [...recipients].sort((left, right) => left.name.localeCompare(right.name, 'pt-BR', { sensitivity: 'base' })),
    [recipients],
  );

  const filteredRecipients = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase('pt-BR');
    if (!normalizedSearch) return sortedRecipients;

    return sortedRecipients.filter(
      recipient =>
        recipient.name.toLocaleLowerCase('pt-BR').includes(normalizedSearch) || recipient.email.toLocaleLowerCase('pt-BR').includes(normalizedSearch),
    );
  }, [sortedRecipients, search]);

  const totalPages = Math.max(1, Math.ceil(filteredRecipients.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const paginatedRecipients = filteredRecipients.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  if (recipients.length === 0) {
    return (
      <div className={`${styles.recipients} ${styles['recipients-empty']}`}>
        <p>Nenhum usuário selecionado. Enquanto a lista estiver vazia, o aviso não é enviado para ninguém.</p>
        <ButtonSecondary theme="blue" size="md" type="button" className={styles['recipients-add-button']} onClick={onAddClick}>
          Adicionar usuários
        </ButtonSecondary>
      </div>
    );
  }

  return (
    <div className={styles.recipients}>
      <div className={styles['recipients-toolbar']} data-testid="recipients-toolbar">
        <SearchInput
          aria-label="Buscar destinatário por nome ou e-mail"
          className={styles['recipients-search']}
          placeholder="Buscar por nome ou e-mail"
          value={search}
          onChange={event => handleSearch(event.target.value)}
        />
        <ButtonSecondary theme="blue" size="md" type="button" className={styles['recipients-add-button']} onClick={onAddClick}>
          Adicionar usuários
        </ButtonSecondary>
      </div>

      {paginatedRecipients.length > 0 ? (
        <ul className={styles['recipients-list']}>
          {paginatedRecipients.map(recipient => (
            <li key={recipient.cdUsuario} className={styles['recipient-row']}>
              <div className={styles['recipient-identity']}>
                <strong>{recipient.name}</strong>
                <span>{recipient.email}</span>
              </div>
              <button type="button" className={styles['remove-button']} onClick={() => onRemove(recipient)}>
                <img src={removeRecipientIcon} alt="" aria-hidden="true" />
                Remover
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className={styles['recipients-feedback']}>
          <p>Nenhum usuário encontrado para &quot;{search}&quot;.</p>
          <button type="button" onClick={() => handleSearch('')}>
            Limpar busca
          </button>
        </div>
      )}

      {totalPages > 1 && (
        <Pagination
          className={styles.pagination}
          total={filteredRecipients.length}
          displayLength={PAGE_SIZE}
          activePage={currentPage + 1}
          showLengthMenu={false}
          showInfo={false}
          onChangePage={nextPage => setPage(nextPage - 1)}
        />
      )}
    </div>
  );
};
